import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Drawer,
  Typography,
  Tabs,
  Tab,
  Chip,
  Button,
  IconButton,
  Skeleton,
  Alert,
  alpha,
  useTheme,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Snackbar,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Info as InfoIcon,
  LocalShipping as ShippingIcon,
  CompareArrows as CrossRefIcon,
  Inventory as InventoryIcon,
  AccountTree as BOMIcon,
  Science as ExplorationIcon,
  AutoAwesome as AIIcon,
  ErrorOutline as ErrorIcon,
} from '@mui/icons-material';
import {
  usePartById,
  useCrossReferencesByPartId,
  useSuppliersByPartId,
  useWhereUsed,
  useFFFEquivalents,
  useEnrichmentResult,
  useApproveEnrichmentResult,
  suppliersKeys,
} from '../hooks';
import PartDetailsTab from './PartDetailsTab';
import PartSuppliersTab from './PartSuppliersTab';
import CrossReferencesTab from './CrossReferencesTab';
import PartEnrichmentReview from './PartEnrichmentReview';
import PartDocumentsDrawer from './PartDocumentsDrawer';
import buyerAgentService from '../services/buyerAgentService';

// ============================================
// TYPES
// ============================================

interface PartDetailDrawerProps {
  partId: string | null;
  onClose: () => void;
  initialTab?: number;
  /** Set by callers that opened the drawer from a non-canonical parent (e.g.
   * a BOM, a review, cross-references). When "Open full page" is clicked, we
   * round-trip this to the next route's `location.state` so AppLayout renders
   * a "← {backLabel}" chip. Optional `returnTab` lets the source page restore
   * its in-page tab state on return. */
  backTo?: string;
  backLabel?: string;
  returnTab?: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
    {value === index && children}
  </Box>
);

const colors = {
  navy: '#1e3a5f',
  slate: '#475569',
  steel: '#64748b',
  teal: '#0f766e',
  azure: '#0369a1',
  amber: '#b45309',
  coral: '#b91c1c',
  lightBg: '#f8fafc',
};

// ============================================
// COMPONENT
// ============================================

const PartDetailDrawer: React.FC<PartDetailDrawerProps> = ({ partId, onClose, initialTab, backTo, backLabel, returnTab }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Tab state
  const [tabValue, setTabValue] = useState(initialTab ?? 0);
  const [documentsDrawerOpen, setDocumentsDrawerOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const [fffDialogOpen, setFFFDialogOpen] = useState(false);
  const [fffSearchEnabled, setFFFSearchEnabled] = useState(false);
  const [enrichmentReviewOpen, setEnrichmentReviewOpen] = useState(false);

  // Reset tab when partId or initialTab changes
  useEffect(() => {
    setTabValue(initialTab ?? 0);
    setFFFSearchEnabled(false);
    setFFFDialogOpen(false);
    setEnrichmentReviewOpen(false);
  }, [partId, initialTab]);

  // DATA HOOKS
  const { data: part, isLoading, error, refetch: refetchPart } = usePartById(partId || '');
  const { data: crossRefsData, isLoading: crossRefsLoading } = useCrossReferencesByPartId(partId || '');
  const { data: suppliers, isLoading: suppliersLoading } = useSuppliersByPartId(partId || '');
  const { data: whereUsed, isLoading: whereUsedLoading } = useWhereUsed(partId || '');

  // RFQ history for audit trail
  const [partRfqs, setPartRfqs] = useState<import('../services/buyerAgentService').RfqDocument[]>([]);

  useEffect(() => {
    if (!partId) return;
    buyerAgentService.getRfqsByPart(partId)
      .then(setPartRfqs)
      .catch(() => {});
  }, [partId]);

  const rfqTimelineEntries = useMemo(() =>
    partRfqs.map((rfq) => ({
      rfqNumber: rfq.rfqNumber,
      rfqStatus: rfq.rfqStatus,
      requestedAt: rfq.requestedAt,
      sentAt: rfq.sentAt,
      respondedAt: rfq.respondedAt,
      closedAt: rfq.closedAt,
      partnerId: rfq.partnerId,
      partnerName: rfq.partnerName,
      quoteCount: rfq.quotes?.length || 0,
      latestQuoteStatus: rfq.quotes?.[rfq.quotes.length - 1]?.reviewStatus,
    })),
    [partRfqs],
  );

  // FFF Equivalents
  const {
    data: fffData,
    isLoading: fffLoading,
    refetch: refetchFFF,
  } = useFFFEquivalents(
    partId || '',
    { limit: 10, minFFFScore: 0.3, requireSameCategory: true },
    fffSearchEnabled && !!partId,
  );

  // Market Explorer
  const partUuid = (part as any)?._id as string | undefined;
  const explorerStatus = (part as any)?.marketExplorerStatus as string | undefined;
  const enrichmentResultQuery = useEnrichmentResult(
    partUuid || partId || '',
    (enrichmentReviewOpen || explorerStatus === 'COMPLETED') && !!part,
  );
  const approveMutation = useApproveEnrichmentResult();

  // Material Exploration
  const materialExplorationStatus = (part as any)?.materialExplorationStatus as string | undefined;
  const isMaterialExploring = materialExplorationStatus === 'IN_PROGRESS';
  const hasExploration = materialExplorationStatus && materialExplorationStatus !== 'NONE';

  // Poll for part status while exploration is in progress
  useEffect(() => {
    if (!isMaterialExploring || !partId) return;
    const interval = setInterval(() => { refetchPart(); }, 3000);
    return () => clearInterval(interval);
  }, [isMaterialExploring, partId, refetchPart]);

  // Poll for market explorer status
  const isExploring = explorerStatus === 'IN_PROGRESS' || explorerStatus === 'QUEUED';
  useEffect(() => {
    if (!isExploring || !partId) return;
    const interval = setInterval(() => { refetchPart(); }, 3000);
    return () => clearInterval(interval);
  }, [isExploring, partId, refetchPart]);

  // Auto-refetch enrichment result when exploration completes
  useEffect(() => {
    if (explorerStatus === 'COMPLETED' && partUuid) {
      enrichmentResultQuery.refetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [explorerStatus, partUuid]);

  // Cross-references extraction
  const crossRefs = Array.isArray(crossRefsData)
    ? crossRefsData
    : (crossRefsData as any)?.alternates || [];
  const crossRefCount = Array.isArray(crossRefsData)
    ? crossRefsData.length
    : (crossRefsData as any)?.totalAlternates || crossRefs.length;

  // HANDLERS
  const handleApproveEnrichment = (approvedFields: string[], rejectedFields: string[]) => {
    const resultId = enrichmentResultQuery.data?._id;
    const id = partUuid || partId;
    if (!id || !resultId) return;
    approveMutation.mutate(
      { partId: id, resultId, approvedFields, rejectedFields },
      {
        onSuccess: () => {
          setEnrichmentReviewOpen(false);
          setSnackbar({ open: true, message: 'Enrichment applied successfully.', severity: 'success' });
          refetchPart();
          if (partId) {
            queryClient.invalidateQueries({ queryKey: suppliersKeys.byPart(partId) });
          }
        },
        onError: (err: any) => {
          setSnackbar({ open: true, message: `Apply failed: ${err.message}`, severity: 'error' });
        },
      },
    );
  };

  const handleFindEquivalents = () => {
    setFFFSearchEnabled(true);
    setFFFDialogOpen(true);
    if (fffData) refetchFFF();
  };

  const handleOpenFullPage = () => {
    if (partId) {
      navigate(`/parts/${partId}`, {
        state: backTo && backLabel
          ? { backTo, backLabel, returnTab }
          : undefined,
      });
    }
    onClose();
  };

  // Don't render internals when closed
  if (!partId) return null;

  return (
    <>
      <Drawer
        anchor="right"
        open={!!partId}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: '100%', md: '80vw', lg: '70vw' },
            maxWidth: 1200,
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        {/* ==================== STICKY HEADER ==================== */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            bgcolor: 'background.paper',
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', p: 2, pb: 1.5 }}>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {isLoading ? (
                <>
                  <Skeleton width={200} height={28} />
                  <Skeleton width={300} height={20} />
                </>
              ) : error ? (
                <Typography color="error">Failed to load part</Typography>
              ) : part ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Typography
                      variant="h6"
                      fontWeight={700}
                      fontFamily="'JetBrains Mono', monospace"
                      sx={{ color: colors.navy }}
                    >
                      {part.partNumber}
                    </Typography>
                    {part.status && (
                      <Chip
                        label={part.status}
                        size="small"
                        sx={{
                          fontSize: '0.65rem',
                          height: 20,
                          fontWeight: 600,
                          bgcolor: part.status === 'ACTIVE' ? alpha(colors.teal, 0.1) : alpha(colors.slate, 0.1),
                          color: part.status === 'ACTIVE' ? colors.teal : colors.slate,
                        }}
                      />
                    )}
                    {(part.manufacturer?.name || part.manufacturerName) && (
                      <Typography variant="body2" color="text.secondary">
                        {part.manufacturer?.name || part.manufacturerName}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    noWrap
                    sx={{ maxWidth: 700 }}
                  >
                    {part.shortDescription || part.description || ''}
                  </Typography>
                </>
              ) : null}
            </Box>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 2, flexShrink: 0 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                onClick={handleOpenFullPage}
                sx={{ textTransform: 'none', fontSize: '0.75rem' }}
              >
                Open Full Page
              </Button>
              <IconButton onClick={onClose} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* TABS */}
          {part && (
            <Tabs
              value={tabValue}
              onChange={(_, v) => setTabValue(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                px: 2,
                minHeight: 40,
                '& .MuiTab-root': { color: colors.slate, minHeight: 40, py: 1, fontSize: '0.8rem' },
                '& .Mui-selected': { color: colors.navy },
                '& .MuiTabs-indicator': { bgcolor: colors.navy },
              }}
            >
              <Tab icon={<InfoIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Details" />
              <Tab icon={<ShippingIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Suppliers (${suppliers?.length || 0})`} />
              <Tab icon={<CrossRefIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Alternatives (${crossRefCount})`} />
              <Tab icon={<InventoryIcon sx={{ fontSize: 16 }} />} iconPosition="start" label="Inventory" />
              <Tab icon={<BOMIcon sx={{ fontSize: 16 }} />} iconPosition="start" label={`Where Used (${whereUsed?.length || 0})`} />
              <Tab
                icon={
                  isMaterialExploring
                    ? <CircularProgress size={14} thickness={5} sx={{ color: colors.navy }} />
                    : materialExplorationStatus === 'FAILED'
                      ? <ErrorIcon sx={{ fontSize: 16 }} color="error" />
                      : <ExplorationIcon sx={{ fontSize: 16, color: materialExplorationStatus === 'COMPLETED' ? colors.teal : colors.slate }} />
                }
                iconPosition="start"
                label={
                  isMaterialExploring
                    ? 'Exploring...'
                    : materialExplorationStatus === 'COMPLETED'
                      ? 'Exploration'
                      : materialExplorationStatus === 'FAILED'
                        ? 'Exploration (Failed)'
                        : 'Exploration'
                }
                sx={{ display: hasExploration ? 'flex' : 'none' }}
              />
            </Tabs>
          )}
        </Box>

        {/* ==================== SCROLLABLE CONTENT ==================== */}
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          {isLoading ? (
            <Box sx={{ p: 3 }}>
              <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2, mb: 2 }} />
              <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
            </Box>
          ) : error ? (
            <Box sx={{ p: 3 }}>
              <Alert severity="error">Failed to load part data: {(error as Error).message}</Alert>
            </Box>
          ) : part ? (
            <Box sx={{ px: 0 }}>
              {/* TAB 0: Details */}
              <TabPanel value={tabValue} index={0}>
                <Box sx={{ px: 3 }}>
                  <PartDetailsTab part={part} partId={partId!} />
                </Box>
              </TabPanel>

              {/* TAB 1: Suppliers */}
              <TabPanel value={tabValue} index={1}>
                <Box sx={{ px: 3 }}>
                  <PartSuppliersTab
                    suppliers={suppliers}
                    isLoading={suppliersLoading}
                    partNumber={part?.partNumber}
                    manufacturerPartNumber={part?.manufacturerPartNumber}
                    manufacturerName={part?.manufacturerName}
                    rfqTimeline={rfqTimelineEntries}
                    onRequestQuote={async (supplier) => {
                      // Same route as the part page: a draft opens for review, nothing is sent from here.
                      if (!part) return;
                      const rfq = await buyerAgentService.createRfq({
                        partnerId: supplier.supplierId,
                        partnerName: supplier.supplier?.name || 'Unknown Supplier',
                        rfqType: 'SINGLE_PART',
                        partIds: [part._id],
                        lineItems: [
                          {
                            partId: part._id,
                            partNumber: part.partNumber,
                            description: part.description,
                            manufacturerName: part.manufacturerName,
                            manufacturerPartNumber: part.manufacturerPartNumber,
                            requestedQuantity: 1,
                            unit: 'EA',
                          },
                        ],
                      });
                      navigate(`/rfq/${rfq._id}`);
                    }}
                  />
                </Box>
              </TabPanel>

              {/* TAB 2: Alternatives */}
              <TabPanel value={tabValue} index={2}>
                <Box sx={{ px: 3 }}>
                  <CrossReferencesTab
                    crossReferences={crossRefs}
                    originalPart={{
                      partNumber: part.partNumber,
                      description: part.description || part.shortDescription,
                      listPrice: part.pricing?.listPrice,
                      manufacturerName: part.manufacturer?.name || part.manufacturerName,
                    }}
                    isLoading={crossRefsLoading}
                    onFindEquivalents={handleFindEquivalents}
                    onViewPart={(alternatePartId) => {
                      navigate(`/parts/${alternatePartId}`);
                      onClose();
                    }}
                    onCompareParts={(alternatePartId) => {
                      navigate(`/cross-references/compare/${partId}/${alternatePartId}`);
                      onClose();
                    }}
                  />
                </Box>
              </TabPanel>

              {/* TAB 3: Inventory (simplified for drawer) */}
              <TabPanel value={tabValue} index={3}>
                <Box sx={{ px: 3, textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  <InventoryIcon sx={{ fontSize: 48, mb: 2, color: theme.palette.grey[300] }} />
                  <Typography variant="body1" gutterBottom>Inventory data</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    View full inventory details including location map on the full page.
                  </Typography>
                  <Button variant="outlined" size="small" onClick={handleOpenFullPage} sx={{ textTransform: 'none' }}>
                    Open Full Page
                  </Button>
                </Box>
              </TabPanel>

              {/* TAB 4: Where Used */}
              <TabPanel value={tabValue} index={4}>
                <Box sx={{ px: 3 }}>
                  {whereUsedLoading ? (
                    <Skeleton variant="rectangular" height={200} sx={{ borderRadius: 2 }} />
                  ) : whereUsed && whereUsed.length > 0 ? (
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: colors.lightBg }}>
                            <TableCell sx={{ fontWeight: 600, color: colors.navy }}>BOM</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.navy }}>Equipment</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.navy }} align="center">Qty</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.navy }}>Position</TableCell>
                            <TableCell sx={{ fontWeight: 600, color: colors.navy }}>Criticality</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {whereUsed.map((wu: any, i: number) => (
                            <TableRow key={i} hover>
                              <TableCell>
                                <Typography variant="body2" fontWeight={600} color={colors.navy}>
                                  {wu.bomName || wu.bomNumber}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {wu.parentPartNumber}
                                </Typography>
                              </TableCell>
                              <TableCell>{wu.equipmentName || wu.equipmentModel || '\u2014'}</TableCell>
                              <TableCell align="center">{wu.quantity}</TableCell>
                              <TableCell>{wu.position || '\u2014'}</TableCell>
                              <TableCell>
                                <Chip
                                  label={wu.criticality || 'STANDARD'}
                                  size="small"
                                  sx={{
                                    bgcolor: alpha(
                                      wu.criticality === 'CRITICAL' ? colors.coral
                                        : wu.criticality === 'ESSENTIAL' ? colors.amber
                                        : colors.slate,
                                      0.1,
                                    ),
                                    color:
                                      wu.criticality === 'CRITICAL' ? colors.coral
                                        : wu.criticality === 'ESSENTIAL' ? colors.amber
                                        : colors.slate,
                                    fontWeight: 500,
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                      <BOMIcon sx={{ fontSize: 48, mb: 2, color: theme.palette.grey[300] }} />
                      <Typography variant="body1">Part not found in any BOM</Typography>
                    </Box>
                  )}
                </Box>
              </TabPanel>

            </Box>
          ) : null}
        </Box>
      </Drawer>

      {/* Nested: Documents Drawer */}
      <PartDocumentsDrawer
        open={documentsDrawerOpen}
        onClose={() => setDocumentsDrawerOpen(false)}
        partId={partId || ''}
        partNumber={part?.partNumber || ''}
      />

      {/* Nested: Enrichment Review */}
      {enrichmentReviewOpen && enrichmentResultQuery.data && (
        <PartEnrichmentReview
          open={enrichmentReviewOpen}
          onClose={() => setEnrichmentReviewOpen(false)}
          part={part!}
          enrichmentResult={enrichmentResultQuery.data}
          onApprove={handleApproveEnrichment}
          isApproving={approveMutation.isPending}
        />
      )}

      {/* Nested: FFF Equivalents Dialog */}
      <Dialog open={fffDialogOpen} onClose={() => setFFFDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon sx={{ color: colors.teal }} />
            <Typography variant="h6" component="span">AI-Powered Equivalent Parts (FFF)</Typography>
          </Box>
          <IconButton onClick={() => setFFFDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {fffLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : fffData && (fffData as any).matches?.length > 0 ? (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>Part Number</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Manufacturer</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">FFF Score</TableCell>
                    <TableCell sx={{ fontWeight: 600 }} align="center">Confidence</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(fffData as any).matches.map((match: any) => (
                    <TableRow key={match.partId} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600} fontFamily="monospace">
                          {match.partNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>{match.description || '\u2014'}</TableCell>
                      <TableCell>{match.manufacturerName || '\u2014'}</TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${Math.round(match.fffScore * 100)}%`}
                          size="small"
                          sx={{
                            bgcolor: match.fffScore >= 0.7 ? alpha(colors.teal, 0.1) : alpha(colors.amber, 0.1),
                            color: match.fffScore >= 0.7 ? colors.teal : colors.amber,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={match.confidence || 'MEDIUM'} size="small" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No equivalent parts found</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFFFDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default PartDetailDrawer;
